import * as rxjs from 'rxjs'

/**
 * Similar to {@link rxjs.skipUntil}, but instead of discarding emissions, this
 * delays emissions until the given observable has its first emission
 *
 * @example
 * // apiRequest$:   |---(1)------(2)-----------(3)----(4)
 * // apiReady$:     |-----------------(*)
 * // Result:        |-----------------(1)(2)---(3)----(4)
 * apiRequest$
 *   .pipe(rxjsUtils.delayUntil(apiReady$))
 *   .subscribe(request => execute(request))
 */
export function delayUntil<T>(
  notifier$: rxjs.Observable<any>,
  { maxDelayed = Infinity }: delayUntil.Options = {},
): rxjs.MonoTypeOperatorFunction<T> {
  return source$ =>
    new rxjs.Observable<T>(subscriber => {
      let notified = false
      const delayedEmissions: T[] = []
      // Stores delayed emissions and emits live emissions
      const sourceSubscriber = source$.subscribe({
        complete: () => subscriber.complete(),
        error: error => subscriber.error(error),
        next: value => {
          if (notified) {
            subscriber.next(value)
          } else {
            delayedEmissions.push(value)
            if (delayedEmissions.length > maxDelayed) {
              // If we have too many delayed emissions, this discards the oldest
              // one
              delayedEmissions.shift()
            }
          }
        },
      })
      // Emits delayed emissions
      const notifierSubscriber = notifier$.subscribe(() => {
        notified = true
        for (const delayedEmission of delayedEmissions) {
          subscriber.next(delayedEmission)
        }
        // We empty the delayed emissions, just in case there is a race
        // condition that causes this to trigger again before the unsubscription
        // completes (not sure if that is even possilbe)
        delayedEmissions.length = 0
        notifierSubscriber.unsubscribe()
      })
      return () => {
        sourceSubscriber.unsubscribe()
        notifierSubscriber.unsubscribe()
      }
    })
}
export namespace delayUntil {
  export interface Options {
    /**
     * Determines the maximum number of delayed emissions. When this number is
     * exceeded, the oldest emissions are discarded
     *
     * @default Infinity
     * @example
     * // maxDelayed = 1
     * // --------------
     * // apiRequest$:   |---(1)------(2)-----------(3)----(4)
     * // apiReady$:     |-----------------(*)
     * // Result:        |-----------------(2)------(3)----(4)
     * apiRequest$
     *   .pipe(rxjsUtils.delayUntil(apiReady$, { maxDelayed: 1 }))
     *   .subscribe(request => execute(request))
     *
     * // maxDelayed = 2
     * // --------------
     * // apiRequest$:   |---(1)------(2)--(3)----(4)----------(5)---(6)
     * // apiReady$:     |-----------------------------(*)
     * // Result:        |-----------------------------(3)(4)--(5)---(6)
     * apiRequest$
     *   .pipe(rxjsUtils.delayUntil(apiReady$, { maxDelayed: 2 }))
     *   .subscribe(request => execute(request))
     */
    maxDelayed?: number
  }
}
